import * as THREE from 'three';
import {
  useState,
  useMemo,
  useContext,
  useEffect,
  SetStateAction,
} from 'react';
import { Button, Card, Flex, Slider, Splitter, Tooltip } from 'antd';
import { ASSETS_2022_CDN, EDITOR_MENU_LOGOS_URL } from 'util/constants';
import EditorButtonGroup from './EditorButtonsGroup';
import { useUserScenesQuery } from '@src/components/generated/graphql';
import {
  ASSET_CATEGORY_TYPES,
  ASSET_PREVIEW_TYPES,
} from 'src/types/assetTypes';
import { ThemeContext } from 'pages/_app';
import styles from './editor-composer.module.css';
import { PoweroffOutlined } from '@ant-design/icons';
import { ASSET_TYPE } from '@/components/scene/react-three-components/sceneAsset';

type EditorComposeProps = {
  assets: any[];
  sceneId: string;
  selectObject: (threeObj: any) => void;
  selectedModel: any;
  camera: THREE.Camera;
  cameraControls: any;
  deleteSelectedModel: () => void;
  undo: () => void;
  redo: () => void;
  isTransformControlsActive: boolean;
  editorMode: any;
  setEditorMode: React.Dispatch<SetStateAction<any>>;
  setIsTransformControlsActive: React.Dispatch<SetStateAction<boolean>>;
  setIsEditorDrawerOpen: React.Dispatch<SetStateAction<boolean>>;
  setIsMaterialDrawerOpen: React.Dispatch<SetStateAction<boolean>>;
  setSelectedModel: React.Dispatch<SetStateAction<any>>;
  setSelectableAssets: React.Dispatch<SetStateAction<any>>;
  setIsLinkModalOpen: React.Dispatch<SetStateAction<boolean>>;
  setIsPOIModalOpen: React.Dispatch<SetStateAction<boolean>>;
  setIsPortalModalOpen: React.Dispatch<SetStateAction<boolean>>;
  setIsLightDrawerOpen: React.Dispatch<SetStateAction<boolean>>;
  setIsShaderDrawerOpen: React.Dispatch<SetStateAction<boolean>>;
  isTimelineMode: boolean;
};

const categories = [
  {
    name: 'All categories',
    id: ASSET_CATEGORY_TYPES.ALL,
  },
  {
    name: 'Images',
    id: ASSET_CATEGORY_TYPES.IMAGE_PLANE,
  },
  {
    name: '3D Models',
    id: ASSET_CATEGORY_TYPES.MODELS,
  },
  {
    name: 'Videos',
    id: ASSET_CATEGORY_TYPES.VIDEO_PLANE,
  },
  {
    name: 'Audios',
    id: ASSET_CATEGORY_TYPES.POSITIONAL_AUDIO,
  },
  {
    name: 'Screen Shares',
    id: ASSET_CATEGORY_TYPES.SCREEN_SHARE,
  },
  {
    name: 'AI Avatars',
    id: ASSET_CATEGORY_TYPES.AI_AVATARS,
  },
  {
    name: 'NPC Avatars',
    id: ASSET_CATEGORY_TYPES.NPC_AVATARS,
  },
  {
    name: 'Shaders',
    id: ASSET_CATEGORY_TYPES.WATER_SHADER,
  },
  {
    name: 'Other',
    id: ASSET_CATEGORY_TYPES.UNKNOWN,
  },
];

export default function EditorComposer({
  assets,
  selectObject,
  selectedModel,
  camera,
  cameraControls,
  deleteSelectedModel,
  undo,
  redo,
  isTransformControlsActive,
  editorMode,
  setEditorMode,
  setIsTransformControlsActive,
  setIsEditorDrawerOpen,
  setIsMaterialDrawerOpen,
  setSelectedModel,
  setSelectableAssets,
  setIsLinkModalOpen,
  setIsPOIModalOpen,
  setIsPortalModalOpen,
  isTimelineMode,
  setIsLightDrawerOpen,
  setIsShaderDrawerOpen,
  sceneId,
}: EditorComposeProps) {
  const [selectedCategory, setSelectedCategory] =
    useState<ASSET_CATEGORY_TYPES>(ASSET_CATEGORY_TYPES.ALL);
  const [intensities, setIntensities] = useState<Array<number>>([]);
  const { isDark } = useContext(ThemeContext);
  const assetsFiltered = useMemo(
    () => filterAssets(assets),
    [selectedCategory, assets]
  );

  const { data } = useUserScenesQuery({
    variables: {
      where: {
        id: {
          equals: sceneId as any,
        },
      },
    },
    fetchPolicy: 'no-cache',
  });

  const scene = data?.userScenes[0];

  const getAssetPreview = useMemo(() => {
    return (asset: any) => {
      if (!asset?.props?.asset) {
        return null;
      }

      const assetData = asset.props.asset;

      if (assetData.type === ASSET_CATEGORY_TYPES.POSITIONAL_AUDIO) {
        return ASSET_PREVIEW_TYPES.AUDIO_PREVIEW;
      }

      if (
        assetData.type === ASSET_CATEGORY_TYPES.VIDEO_PLANE ||
        assetData.type === 'VIDEO_PLANE'
      ) {
        if (assetData.previewImage) {
          return assetData.previewImage;
        }
        return ASSET_PREVIEW_TYPES.VIDEO_PREVIEW;
      }

      if (assetData.type === ASSET_CATEGORY_TYPES.INITIAL_POINT) {
        return ASSET_PREVIEW_TYPES.INITIAL_POINT_PREVIEW;
      }

      if (assetData.type === ASSET_CATEGORY_TYPES.WATER_SHADER) {
        return ASSET_PREVIEW_TYPES.WATER_SHADER_PREVIEW;
      }

      if (assetData.isPortal === true) {
        return ASSET_PREVIEW_TYPES.PORTAL_PREVIEW;
      }

      if (
        (assetData.type === ASSET_CATEGORY_TYPES.MODELS ||
          assetData.type === 'OTHER') &&
        assetData.url &&
        assetData.url.includes('/assets/')
      ) {
        return ASSET_PREVIEW_TYPES.MODEL_PREVIEW;
      }

      if (assetData.previewImage) {
        return assetData.previewImage;
      }

      if (assetData.url) {
        return assetData.url;
      }

      return null;
    };
  }, []);

  function filterAssets(assets) {
    if (!assets || !assets.length) {
      return [];
    }

    if (selectedCategory === ASSET_CATEGORY_TYPES.ALL) {
      return assets;
    }

    if (selectedCategory === ASSET_CATEGORY_TYPES.UNKNOWN) {
      return assets.filter(
        (el) =>
          !Object.values(ASSET_CATEGORY_TYPES).includes(el.props?.asset?.type)
      );
    }

    return assets.filter((el) => {
      const assetType = el.props?.asset?.type;
      if (selectedCategory === ASSET_CATEGORY_TYPES.VIDEO_PLANE) {
        return (
          assetType === ASSET_CATEGORY_TYPES.VIDEO_PLANE ||
          assetType === 'VIDEO_PLANE'
        );
      }
      return assetType === selectedCategory;
    });
  }

  function onSelectCard(asset) {
    const threeObj = asset.props?.asset.ref.current;
    selectObject(threeObj);

    const targetPos = new THREE.Vector3();
    threeObj.getWorldPosition(targetPos);

    const offset = new THREE.Vector3(0, 2, 5); // adjust for distance
    const newPos = targetPos.clone().add(offset);

    camera.position.copy(newPos);
    cameraControls.lookAtObject(threeObj);
  }

  useEffect(() => {
    const intensitiesArray = assetsFiltered.map((asset) => {
      const light = asset.props?.light;
      if (!light) return 0;
      return typeof light.intensity === 'number' ? light.intensity : 0;
    });
    setIntensities(intensitiesArray);
  }, [assetsFiltered]);

  const getAssetLight = (asset: any) => {
    const refCurrent = asset.props?.light?.ref?.current;
    if (!refCurrent) return null;
    return refCurrent;
  };

  const onLightIntensityChange = (asset, index) => (value: number) => {
    const light = getAssetLight(asset);
    if (!light || typeof light.intensity !== 'number') return;
    const newIntensities = [...intensities];
    light.intensity = value;
    newIntensities[index] = value;
    if (asset.props.light) {
      asset.props.light.intensity = value;
    }
    setIntensities(newIntensities);
  };

  const setLightVisible = (asset) => {
    const light = getAssetLight(asset);
    if (!light) return;
    light.visible = !light.visible;
  };

  function isSameAsset(selectedAsset, currentAsset) {
    const selectedAssetUUID = selectedAsset?.props?.asset.ref.current?.uuid;
    const currentAssetUUID = currentAsset.props?.asset.ref.current?.uuid;

    if (
      !selectedAsset ||
      !currentAsset ||
      !currentAssetUUID ||
      !selectedAssetUUID
    ) {
      return false;
    }

    const selectedAssetDBId = selectedAsset?.props?.asset?.id;
    const currentAssetDBId = currentAsset?.props?.asset?.id;

    if (!selectedAssetDBId || !currentAssetDBId) {
      return selectedAssetUUID === currentAssetUUID;
    }

    return selectedAssetDBId === currentAssetDBId;
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Delete') {
        deleteSelectedModel();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <div
      className={`${styles.root} composer-root`}
      style={{
        backgroundColor: isDark ? '#212020' : '#f4f4f4',
      }}
    >
      <Splitter>
        <Splitter.Panel defaultSize={250} min={0} max={250}>
          <div className={styles.categories}>
            <span className={styles.category}>Filter by category</span>
            <div className={styles.categoriesList}>
              {categories.map((category, index) => (
                <span
                  key={`${category.id}-${index}`}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`${styles.categoryName} ${
                    selectedCategory === category.id && styles.selectedCategory
                  }`}
                >
                  {category.name}
                </span>
              ))}
            </div>
          </div>
        </Splitter.Panel>
        <Splitter.Panel>
          <EditorButtonGroup
            undo={undo}
            isDisplayMinimapEnabled={!!scene?.displayMinimap}
            redo={redo}
            isTransformControlsActive={isTransformControlsActive}
            editorMode={editorMode}
            setEditorMode={setEditorMode}
            deleteSelectedModel={deleteSelectedModel}
            setIsTransformControlsActive={setIsTransformControlsActive}
            setIsEditorDrawerOpen={setIsEditorDrawerOpen}
            setIsMaterialDrawerOpen={setIsMaterialDrawerOpen}
            selectedModel={selectedModel}
            setSelectedModel={setSelectedModel}
            setSelectableAssets={setSelectableAssets}
            setIsLinkModalOpen={setIsLinkModalOpen}
            setIsPOIModalOpen={setIsPOIModalOpen}
            setIsPortalModalOpen={setIsPortalModalOpen}
            isTimelineMode={isTimelineMode}
            setIsLightDrawerOpen={setIsLightDrawerOpen}
            setIsShaderDrawerOpen={setIsShaderDrawerOpen}
            assets={assets}
            isLightAsset={
              selectedModel?.props?.asset?.type === ASSET_TYPE.MISC.LIGHT
            }
          />
          <div className={styles.assets}>
            {assetsFiltered.length ? (
              assetsFiltered.map((asset, index) => {
                const previewImage = getAssetPreview(asset);

                return (
                  <Card
                    key={`${asset.props?.asset?.id}-${index}`}
                    title={
                      <span className={styles.cardTitle}>
                        {asset.props?.asset?.name}
                      </span>
                    }
                    className={`${
                      isSameAsset(selectedModel, asset)
                        ? styles.cardSelected
                        : styles.cardDefault
                    }`}
                    onClick={() => onSelectCard(asset)}
                    size='small'
                  >
                    <Flex justify='center'>
                      {asset.props?.asset?.type === ASSET_TYPE.MISC.LIGHT && (
                        <div className='absolute flex flex-col top-12 left-2 w-32'>
                          <Tooltip title='Toggle Light Visibility'>
                            <Button
                              shape='circle'
                              type={
                                getAssetLight(asset)?.visible
                                  ? 'primary'
                                  : 'default'
                              }
                              icon={<PoweroffOutlined />}
                              onClick={() => setLightVisible(asset)}
                            />
                          </Tooltip>
                          <Slider
                            min={0}
                            max={20}
                            step={0.1}
                            onChange={onLightIntensityChange(asset, index)}
                            value={intensities[index]}
                          />
                        </div>
                      )}

                      {previewImage === ASSET_PREVIEW_TYPES.AUDIO_PREVIEW ? (
                        <div className={styles.audioPreview}>
                          <div className={styles.audioIcon}>🔊</div>
                          <div className={styles.audioLabel}>Audio</div>
                        </div>
                      ) : previewImage === ASSET_PREVIEW_TYPES.VIDEO_PREVIEW ? (
                        <div className={styles.videoPreview}>
                          <div className={styles.videoIcon}>🎥</div>
                          <div className={styles.videoLabel}>Video</div>
                        </div>
                      ) : previewImage ===
                        ASSET_PREVIEW_TYPES.INITIAL_POINT_PREVIEW ? (
                        <div className={styles.initialPointPreview}>
                          <div className={styles.initialPointIcon}>📍</div>
                          <div className={styles.initialPointLabel}>
                            Initial Point
                          </div>
                        </div>
                      ) : previewImage ===
                        ASSET_PREVIEW_TYPES.PORTAL_PREVIEW ? (
                        <div className={styles.portalPreview}>
                          <div className={styles.portalIcon}>🚪</div>
                          <div className={styles.portalLabel}>Portal</div>
                        </div>
                      ) : previewImage ===
                        ASSET_PREVIEW_TYPES.WATER_SHADER_PREVIEW ? (
                        <img
                          src='https://d23hwblhugcea5.cloudfront.net/studioAssets/imageAssets/WaterShader.jpg'
                          alt='Water Shader Preview'
                          className={styles.image}
                          onError={(e) => {
                            console.error(
                              'Error loading water shader preview image:',
                              e
                            );
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : previewImage === ASSET_PREVIEW_TYPES.MODEL_PREVIEW ? (
                        <div className={styles.modelPreview}>
                          <div className={styles.modelIcon}>📦</div>
                        </div>
                      ) : previewImage ? (
                        <img
                          src={previewImage}
                          alt={`${asset.props?.asset?.name} preview`}
                          className={styles.image}
                          onError={(e) => {
                            console.error('Error loading preview image:', e);
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : (
                        <img
                          src={`${ASSETS_2022_CDN}/Studio/scene-assets-image.png`}
                          alt='Default preview'
                          className={styles.image}
                        />
                      )}
                    </Flex>
                  </Card>
                );
              })
            ) : (
              <span className={styles.noAssets}>
                Assets added to scene will appear here. Please use{' '}
                <img
                  src={`${EDITOR_MENU_LOGOS_URL}add-to-scene-icon.svg`}
                  alt='add-to-scene'
                />
                "Add to scene" menu option for that.
              </span>
            )}
          </div>
        </Splitter.Panel>
      </Splitter>
    </div>
  );
}
